/**
 * Analytics report export — PDF and CSV built from the data a tab has
 * already loaded, so the file always matches what's on screen for the
 * applied filters.
 *
 * Report shape (built by each tab's `buildReport()`):
 *   {
 *     title:     'Medias Audience Report',
 *     subtitle:  'El Vacilón de La Mañana',      // optional
 *     filters:   [['Period', '…'], ['Category', '…'], …],
 *     kpis:      [['Unique Users', '273'], …],
 *     timeline:  { granularity: 'day', rows: [{ bucket, sessions, users }] } | null,
 *     tables:    [{ title, columns: ['#', 'Title', …], rows: [[…], …] }]
 *   }
 */

const BRAND_INK = [17, 24, 39]
const INK2 = [75, 85, 99]
const RULE = [229, 231, 235]
const PANEL = [248, 250, 252]

/** Bucket key ('2026-10-01', '2026-10-01 14:00', '2026-10') → display label. */
export function formatBucket(key, granularity) {
  if (!key) return ''
  if (granularity === 'day') {
    const [y, m, d] = key.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  if (granularity === 'month') {
    const [y, m] = key.split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }
  return key
}

/** Weekday for day buckets, '' otherwise. */
export function bucketWeekday(key, granularity) {
  if (granularity !== 'day' || !key) return ''
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' })
}

/**
 * Merge the sessions and unique-users timelines into one row per
 * bucket. For day buckets, every day in [dateFrom, dateTo] gets a row
 * (0 when the API returned nothing) so gaps are visible in the report.
 */
export function mergeTimelines({ sessions = [], users = [], granularity, dateFrom, dateTo }) {
  const s = new Map(sessions.map(r => [r._id, r.count || 0]))
  const u = new Map(users.map(r => [r._id, r.count || 0]))
  let keys
  if (granularity === 'day' && dateFrom && dateTo) {
    keys = []
    const d = new Date(dateFrom); d.setHours(0, 0, 0, 0)
    const end = new Date(dateTo)
    while (d <= end) {
      keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)
      d.setDate(d.getDate() + 1)
    }
    // Buckets the API returns outside the local days (its day buckets
    // are UTC, so the range's last bucket can spill into the next day)
    // are left out so the table matches the report period.
  } else {
    keys = [...new Set([...s.keys(), ...u.keys()])].sort()
  }
  return keys.map(k => ({ bucket: k, sessions: s.get(k) || 0, users: u.get(k) || 0 }))
}

export function reportFileName(report, ext) {
  const date = new Date()
  const stamp = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  const name = [report.subtitle, report.title, stamp].filter(Boolean).join(' - ')
  return `${name.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim()}.${ext}`
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

const csvCell = (v) => {
  const s = v == null ? '' : String(v)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}
const csvLine = (cells) => cells.map(csvCell).join(',')

export function downloadCsv(report, { appName = 'LaMusica Platform' } = {}) {
  const lines = []
  lines.push(csvLine([report.title]))
  if (report.subtitle) lines.push(csvLine([report.subtitle]))
  for (const [k, v] of report.filters || []) lines.push(csvLine([k, v]))
  lines.push(csvLine(['Generated by', appName]))
  lines.push('')

  if (report.kpis?.length) {
    lines.push(csvLine(['Metric', 'Value']))
    for (const [k, v] of report.kpis) lines.push(csvLine([k, v]))
    lines.push('')
  }

  const tl = report.timeline
  if (tl?.rows?.length) {
    const isDay = tl.granularity === 'day'
    lines.push(csvLine(isDay ? ['Date', 'Day', 'Sessions', 'Unique Users'] : ['Period', 'Sessions', 'Unique Users']))
    for (const r of tl.rows) {
      lines.push(csvLine(isDay
        ? [r.bucket, bucketWeekday(r.bucket, 'day'), r.sessions, r.users]
        : [r.bucket, r.sessions, r.users]))
    }
    const totS = tl.rows.reduce((a, r) => a + r.sessions, 0)
    const totU = tl.rows.reduce((a, r) => a + r.users, 0)
    lines.push(csvLine(isDay ? ['Total (sum of days)', '', totS, totU] : ['Total (sum of periods)', totS, totU]))
    lines.push('')
  }

  for (const t of report.tables || []) {
    if (!t.rows?.length) continue
    lines.push(csvLine([t.title]))
    lines.push(csvLine(t.columns))
    for (const r of t.rows) lines.push(csvLine(r))
    lines.push('')
  }

  // BOM so Excel opens accents (Vacilón, Mañana) correctly.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
  triggerDownload(blob, reportFileName(report, 'csv'))
}

// ---------------------------------------------------------------------------
// PDF
// ---------------------------------------------------------------------------

// jsPDF's built-in fonts are WinAnsi: keep Latin-1 (accents, ñ), drop
// emoji and other glyphs it can't draw.
const pdfText = (v) => String(v ?? '').replace(/[^\x20-\x7E -ÿ–—’“”•]/g, '').replace(/\s+/g, ' ').trim()

export async function downloadPdf(report, { appName = 'LaMusica Platform' } = {}) {
  const [{ jsPDF }, { autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')])
  const doc = new jsPDF({ unit: 'pt', format: 'letter' })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const M = 48
  const W = pageW - M * 2
  const generatedOn = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

  // Header
  let y = M
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(42, 125, 225)
  doc.text(pdfText(appName).toUpperCase(), M, y)
  y += 22
  doc.setFontSize(20); doc.setTextColor(...BRAND_INK)
  doc.text(pdfText(report.subtitle || report.title), M, y)
  y += 16
  if (report.subtitle) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(11); doc.setTextColor(...INK2)
    doc.text(pdfText(report.title), M, y)
    y += 10
  }
  doc.setDrawColor(...BRAND_INK); doc.setLineWidth(1.2); doc.line(M, y, M + W, y)
  y += 14

  // Filters / meta
  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    theme: 'plain',
    styles: { font: 'helvetica', fontSize: 9, cellPadding: { top: 2, bottom: 2, left: 0, right: 8 }, textColor: BRAND_INK },
    columnStyles: { 0: { fontStyle: 'bold', textColor: INK2, cellWidth: 90 } },
    body: [...(report.filters || []), ['Report date', generatedOn], ['Generated by', appName]].map(([k, v]) => [pdfText(k), pdfText(v)])
  })
  y = doc.lastAutoTable.finalY + 16

  // KPI cards — up to 3 per row
  if (report.kpis?.length) {
    const perRow = 3
    const gap = 10
    const cw = (W - gap * (perRow - 1)) / perRow
    const ch = 52
    report.kpis.forEach(([label, value], i) => {
      const col = i % perRow
      const row = Math.floor(i / perRow)
      const x = M + col * (cw + gap)
      const yy = y + row * (ch + gap)
      doc.setFillColor(...PANEL); doc.setDrawColor(...RULE); doc.setLineWidth(0.5)
      doc.rect(x, yy, cw, ch, 'FD')
      doc.setFillColor(42, 125, 225); doc.rect(x, yy, 3, ch, 'F')
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...INK2)
      doc.text(pdfText(label).toUpperCase(), x + 12, yy + 17)
      doc.setFont('helvetica', 'bold'); doc.setFontSize(17); doc.setTextColor(...BRAND_INK)
      doc.text(pdfText(value), x + 12, yy + 40)
    })
    y += Math.ceil(report.kpis.length / perRow) * (ch + gap) + 12
  }

  const sectionTitle = (text) => {
    if (y > pageH - 120) { doc.addPage(); y = M }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...BRAND_INK)
    doc.text(pdfText(text), M, y)
    y += 8
  }
  const tableStyles = {
    margin: { left: M, right: M, bottom: 50 },
    theme: 'striped',
    styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 4, textColor: BRAND_INK, lineColor: RULE },
    headStyles: { fillColor: BRAND_INK, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [251, 252, 253] },
    footStyles: { fillColor: PANEL, textColor: BRAND_INK, fontStyle: 'bold' }
  }
  const nf = (n) => Number(n || 0).toLocaleString('en-US')

  // Timeline breakdown
  const tl = report.timeline
  if (tl?.rows?.length) {
    const label = { minute: 'Breakdown by minute', hour: 'Breakdown by hour', day: 'Daily breakdown', month: 'Monthly breakdown', year: 'Yearly breakdown' }[tl.granularity] || 'Breakdown'
    sectionTitle(label)
    const isDay = tl.granularity === 'day'
    const totS = tl.rows.reduce((a, r) => a + r.sessions, 0)
    const totU = tl.rows.reduce((a, r) => a + r.users, 0)
    autoTable(doc, {
      ...tableStyles,
      startY: y,
      head: [isDay ? ['Date', 'Day', 'Sessions', 'Unique Users'] : ['Period', 'Sessions', 'Unique Users']],
      body: tl.rows.map(r => isDay
        ? [formatBucket(r.bucket, 'day'), bucketWeekday(r.bucket, 'day'), nf(r.sessions), nf(r.users)]
        : [formatBucket(r.bucket, tl.granularity), nf(r.sessions), nf(r.users)]),
      foot: [isDay ? ['Total (sum of days)', '', nf(totS), nf(totU)] : ['Total', nf(totS), nf(totU)]],
      showFoot: 'lastPage',
      columnStyles: isDay ? { 2: { halign: 'right' }, 3: { halign: 'right' } } : { 1: { halign: 'right' }, 2: { halign: 'right' } },
      didParseCell: (d) => {
        const right = isDay ? d.column.index >= 2 : d.column.index >= 1
        if (right) d.cell.styles.halign = 'right'
      }
    })
    y = doc.lastAutoTable.finalY + 20
  }

  // Extra tables (Top Content)
  for (const t of report.tables || []) {
    if (!t.rows?.length) continue
    sectionTitle(t.title)
    autoTable(doc, {
      ...tableStyles,
      startY: y,
      head: [t.columns.map(pdfText)],
      body: t.rows.map(r => r.map(pdfText)),
      columnStyles: { 0: { cellWidth: 26 }, 2: { cellWidth: 58 }, 3: { cellWidth: 82 }, 4: { cellWidth: 72 } },
      didParseCell: (d) => { if (d.column.index >= 2) d.cell.styles.halign = 'right' }
    })
    y = doc.lastAutoTable.finalY + 20
  }

  // Footer on every page
  const pages = doc.getNumberOfPages()
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i)
    doc.setDrawColor(...RULE); doc.setLineWidth(0.5); doc.line(M, pageH - 36, M + W, pageH - 36)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...INK2)
    doc.text(`Generated by ${pdfText(appName)} · ${generatedOn}`, M, pageH - 22)
    doc.text(`Page ${i} of ${pages}`, M + W, pageH - 22, { align: 'right' })
  }

  triggerDownload(doc.output('blob'), reportFileName(report, 'pdf'))
}

// ---------------------------------------------------------------------------
// Tab → report
// ---------------------------------------------------------------------------

export function formatDurationSeconds(s) {
  s = Math.round(Number(s) || 0)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h) return `${h}h ${m}m`
  if (m) return `${m}m ${sec}s`
  return `${sec}s`
}

const TOP_CONTENT_SECTIONS = [
  ['lives', 'Top Lives'],
  ['shows', 'Top Shows'],
  ['medias', 'Top Medias'],
  ['categories', 'Top Categories']
]

/**
 * Build the report object from a tab's loaded state.
 *   kpiCards    — the tab's KPI cards ({ label, value })
 *   data        — the tab's merged panel data
 *   applied     — applied filters (dateFrom / dateTo)
 *   granularity — fallback bucket size when the timeline doesn't carry one
 */
export function buildTabReport({ title, kpiCards = [], data = {}, applied = {}, granularity = 'day' }) {
  const sessions = data?.viewers?.concurrent || []
  const users = data?.uniqueUsers?.timeline || []
  const g = data?.viewers?.granularity || granularity
  const timeline = (sessions.length || users.length)
    ? { granularity: g, rows: mergeTimelines({ sessions, users, granularity: g, dateFrom: applied.dateFrom, dateTo: applied.dateTo }) }
    : null

  const nf = (n) => Number(n || 0).toLocaleString('en-US')
  const tables = TOP_CONTENT_SECTIONS
    .map(([key, label]) => ({
      title: `${label} (top 10)`,
      columns: ['#', 'Title', 'Sessions', 'Session Duration', 'Watch Time'],
      rows: (data?.topContent?.[key] || []).map((r, i) => [
        i + 1, r.title || r.id, nf(r.count), formatDurationSeconds(r.totalSessionDuration), formatDurationSeconds(r.totalTime)
      ])
    }))
    .filter(t => t.rows.length)

  return {
    title,
    kpis: kpiCards.map(c => [c.label, c.value]),
    timeline,
    tables
  }
}
